import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import { Flag, Movie, Tv, RssFeed } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { useNavigate } from 'react-router-dom';

const client = generateClient();

const API_BASE_URL = "https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api";

const MyReports = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const [postReports, setPostReports] = useState([]);
  const [movieReports, setMovieReports] = useState([]);
  const [showReports, setShowReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (user?.id) {
      fetchAllMyReports();
    }
  }, [user]);

  const fetchAllMyReports = async () => {
    try {
      setLoading(true);
      
      const [postResult, movieResult, showResult] = await Promise.all([
        // Fetch post reports
        client.graphql({
          query: `
            query ListMyPostReports($reportedBy: ID!) {
              listPostReports(filter: { reportedBy: { eq: $reportedBy } }) {
                items {
                  id
                  postID
                  reason
                  description
                  status
                  createdAt
                  post {
                    id
                    content
                    author {
                      id
                      displayName
                      username
                    }
                  }
                }
              }
            }
          `,
          variables: { reportedBy: user.id }
        }),
        // Fetch movie reports
        client.graphql({
          query: `
            query ListMyMovieReports($reportedBy: ID!) {
              listMovieReports(filter: { reportedBy: { eq: $reportedBy } }) {
                items {
                  id
                  movieID
                  reason
                  description
                  status
                  createdAt
                }
              }
            }
          `,
          variables: { reportedBy: user.id }
        }),
        // Fetch show reports
        client.graphql({
          query: `
            query ListMyShowReports($reportedBy: ID!) {
              listShowReports(filter: { reportedBy: { eq: $reportedBy } }) {
                items {
                  id
                  showID
                  reason
                  description
                  status
                  createdAt
                }
              }
            }
          `,
          variables: { reportedBy: user.id }
        })
      ]);
      
      const postReports = postResult.data.listPostReports.items || [];
      const movieReports = movieResult.data.listMovieReports.items || [];
      const showReports = showResult.data.listShowReports.items || [];
      
      // Enrich movie reports with movie data
      const enrichedMovieReports = await Promise.all(
        movieReports.map(async (report) => {
          try {
            const movieResponse = await fetch(`${API_BASE_URL}/movies/${report.movieID}`);
            const movieData = movieResponse.ok ? await movieResponse.json() : null;
            return {
              ...report,
              movie: movieData ? {
                id: movieData._id,
                name: movieData.name,
                poster: movieData.poster
              } : null
            };
          } catch (error) {
            console.warn('Failed to fetch movie data:', error);
            return report;
          }
        })
      );
      
      // Enrich show reports with show data
      const enrichedShowReports = await Promise.all(
        showReports.map(async (report) => {
          try {
            const showResponse = await fetch(`${API_BASE_URL}/shows/${report.showID}`);
            const showData = showResponse.ok ? await showResponse.json() : null;
            return {
              ...report,
              show: showData ? {
                id: showData._id,
                name: showData.name,
                poster: showData.poster
              } : null
            };
          } catch (error) {
            console.warn('Failed to fetch show data:', error);
            return report;
          }
        })
      );
      
      setPostReports(postReports);
      setMovieReports(enrichedMovieReports);
      setShowReports(enrichedShowReports);
    } catch (error) {
      console.error('Error fetching my reports:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load your reports',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'REVIEWED': return 'info';
      case 'RESOLVED': return 'success';
      case 'DISMISSED': return 'default';
      default: return 'default';
    }
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 'PENDING': return 'Your report is being reviewed by our team';
      case 'REVIEWED': return 'Your report has been reviewed and is being processed';
      case 'RESOLVED': return 'Your report has been resolved - appropriate action was taken';
      case 'DISMISSED': return 'Your report was reviewed but no action was required';
      default: return 'Status unknown';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const allReports = [
    ...postReports.map(r => ({ ...r, type: 'post' })),
    ...movieReports.map(r => ({ ...r, type: 'movie' })),
    ...showReports.map(r => ({ ...r, type: 'show' }))
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const getReportsForTab = () => {
    switch (activeTab) {
      case 0: return allReports;
      case 1: return postReports.map(r => ({ ...r, type: 'post' }));
      case 2: return movieReports.map(r => ({ ...r, type: 'movie' }));
      case 3: return showReports.map(r => ({ ...r, type: 'show' }));
      default: return [];
    }
  };

  const renderReportCard = (report) => {
    return (
      <Grid item xs={12} key={`${report.type}-${report.id}`}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box display="flex" gap={1}>
                <Chip 
                  icon={<Flag />}
                  label={report.status}
                  color={getStatusColor(report.status)}
                  size="small"
                />
                <Chip
                  icon={report.type === 'post' ? <RssFeed /> : report.type === 'movie' ? <Movie /> : <Tv />}
                  label={report.type === 'post' ? 'Post' : report.type === 'movie' ? 'Movie' : 'Show'}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(report.createdAt))} ago
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom>
              {report.reason}
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              {getStatusDescription(report.status)}
            </Typography>

            {/* Content-specific display */}
            {report.type === 'post' && report.post && (
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Reported Post:
                </Typography>
                <Typography variant="body2">
                  {report.post.content?.slice(0, 150)}
                  {report.post.content?.length > 150 && '...'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  by {report.post.author?.displayName || 'Unknown User'}
                </Typography>
              </Box>
            )}

            {report.type === 'movie' && (
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Reported Movie:
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  {report.movie?.poster && (
                    <img 
                      src={report.movie.poster} 
                      alt={report.movie.name}
                      style={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => navigate(`/movies/${report.movieID}`)}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <Box>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/movies/${report.movieID}`)}
                    >
                      {report.movie?.name || `Movie ID: ${report.movieID}`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {report.type === 'show' && (
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Reported Show:
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  {report.show?.poster && (
                    <img 
                      src={report.show.poster} 
                      alt={report.show.name}
                      style={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => navigate(`/shows/${report.showID}`)}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <Box>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/shows/${report.showID}`)}
                    >
                      {report.show?.name || `Show ID: ${report.showID}`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {report.description && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Your description:
                </Typography>
                <Typography variant="body2">
                  {report.description}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Reports
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Track the status of content you've reported
      </Typography>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label={`All (${allReports.length})`} />
        <Tab label={`Posts (${postReports.length})`} />
        <Tab label={`Movies (${movieReports.length})`} />
        <Tab label={`Shows (${showReports.length})`} />
      </Tabs>

      {getReportsForTab().length === 0 ? (
        <Alert severity="info">
          {activeTab === 0 ? "You haven't reported any content yet." :
           activeTab === 1 ? "You haven't reported any posts yet." :
           activeTab === 2 ? "You haven't reported any movies yet." :
           "You haven't reported any shows yet."}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {getReportsForTab().map(renderReportCard)}
        </Grid>
      )}
    </Container>
  );
};

export default MyReports;